import React, { PureComponent, createContext } from 'react';
import * as Sentry from '@sentry/react';
import { connect } from 'react-redux';
import Pusher from 'pusher-js';
import { updatePageTitle } from '../common';
import { PUSHER } from '../config/constants';
import store from '../store';
import {
  decrementUnreadOneTapOffersCount,
  incrementUnreadOneTapOffersCount,
} from '../actions/unreadOneTapOffersCountActions';
import { resetNewOffers } from '../actions/offerActions';
import { updateIdVerificationStatus, updateUnreadMessagesCount } from '../actions/profileActions';
import MasterConfig from 'config/Master';
import { getUnreadCount } from 'sdk';

const { Provider, Consumer } = createContext({
  isConnected: false,
  onRealtimeEvent: null,
});

export const ConnectPusher = (ChildComponent: any): any => {
  const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY;
  const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER;
  const API_ENDPOINT = process.env.REACT_APP_SDK_BASE_URL;

  class PusherProvider extends PureComponent {
    constructor() {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      super();

      this.state = {
        channel: null,
      };

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      this.bindedListeners = [];
    }

    componentDidMount() {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      this.pageMonitoring = null;
      this.init();
    }

    componentWillUnmount() {
      this.disconnect();
    }

    /**
     * Initialize connection
     */
    init() {
      try {
        this.connect();
      } catch (error) {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        if (this.connection && typeof this.connection.disconnect === 'function') {
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          this.connection.disconnect();
        }

        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Realtime', action: 'Initialize' } });
        }

        console.error(error);
      }
    }

    /**
     * Connect to pusher and add event listeners
     *
     * @param {number} tryCount
     */
    connect(tryCount = 0) {
      let currentTry = tryCount;

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      if (this.connection && typeof this.connection.subscribe === 'function') {
        this.subscribe();
        return;
      }

      const { virgil, jwToken } = store.getState().auth;

      const pusher = new Pusher(PUSHER_KEY, {
        cluster: PUSHER_CLUSTER,
        forceTLS: true,
        authEndpoint: `${API_ENDPOINT}/auth/socket`,
        auth: {
          headers: {
            Authorization: `Bearer ${jwToken}`,
            'X-Auth-Type': virgil ? 'virgil' : '',
          },
        },
      });

      pusher.connection.bind('error', () => {
        this.disconnect();

        if (currentTry === 3) {
          return console.error('Max pusher connection attempts reached!');
        }

        currentTry += 1;
        this.connect(currentTry);
      });

      pusher.connection.bind('disconnected', () => {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        this.connection = null;
      });

      pusher.connection.bind('connected', () => {
        currentTry = 0;
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        this.connection = pusher;
        this.subscribe();
      });
    }

    /**
     * Disconnect to pusher
     */
    disconnect() {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      if (this.connection && typeof this.connection.disconnect === 'function') {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        this.connection.disconnect();
      }
    }

    /**
     * Subscribe to a channel
     *
     * @param {number} tryCount
     */
    subscribe(tryCount = 0) {
      let currentTry = tryCount;
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const channel = this.connection && this.connection.subscribe(`private-${this.props.account.hash_id}`);

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      if (this.connection) {
        channel.bind('pusher:subscription_succeeded', () => {
          if (MasterConfig.APP_ENV !== 'production') {
            console.log('Pusher channel subscription succeeded');
          }

          currentTry = 0;
          this.setState({ channel }, () => this.listen());
        });

        channel.bind('pusher:subscription_error', status => {
          this.setState({ channel: null });

          if (MasterConfig.APP_ENV !== 'production') {
            console.error(`Pusher channel subscription error with status: ${status}`);
          }

          if (currentTry === 3) {
            this.disconnect();
            return console.error('Max channel subscription attempts reached!');
          }

          if ([401, 408, 503, 403].indexOf(status) > -1) {
            currentTry += 1;
            this.subscribe(currentTry);
          }
        });
      }
    }

    handleNewOfferNotification(data) {
      if (!data || (data && data.type !== 'offers')) return;

      const pathName = window.location.pathname;
      const offersView = localStorage.getItem('offers-view') as string;
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const { incrementUnreadOneTapOffersCountAction, decrementUnreadOneTapOffersCountAction } = this.props;

      if (data.action === 'cancel') {
        decrementUnreadOneTapOffersCountAction();
      } else if (data.action === 'receive' || data.action === 'suggest') {
        if (pathName !== '/offers/new' && (!offersView || offersView === 'grid')) {
          incrementUnreadOneTapOffersCountAction();
        }
      }
    }

    handleIDVeriricationUpdate(data) {
      if (data && data.type !== 'IdVerificationAlerts') return;

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      this.props.updateIdVerificationStatus(data.data);
    }

    /**
     * Listen to events
     */
    listen() {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const { channel } = this.state;
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const { updateNotificationCounts, updateUnreadMessagesCountProp } = this.props;
      const { EVENTS } = PUSHER;

      channel.bind(EVENTS.ACCOUNT_ALERTS, async data => {
        if (MasterConfig.APP_ENV !== 'production') {
          console.log(`Notification Received from Pusher for event - ${EVENTS.ACCOUNT_ALERTS}`, data);
        }

        this.handleNewOfferNotification(data);

        if (data.type === 'video-event') {
          return false;
        }

        const { counts } = data && data.data && data.data.account;

        const unreadMessagesCounts = await getUnreadCount();

        updatePageTitle(unreadMessagesCounts);
        updateNotificationCounts(counts);

        // Add delay to give time for msgs-sls to update unread count
        setTimeout(() => {
          updateUnreadMessagesCountProp(unreadMessagesCounts);
        }, 1000);

        if (data.action === 'cancel' || data.type === 'counts') {
          return false;
        }
      });

      channel.bind(EVENTS.ACCOUNT_IDV_ALERT, data => {
        this.handleIDVeriricationUpdate(data);
      });

      this.monitorPusher();
    }

    monitorPusher() {
      try {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        this.bindedListeners.forEach(listener => listener());
      } catch (error) {
        console.warn(error);
        // no binded listeners available
      }
    }

    /**
     * Bind event and callback to channel
     */
    onRealtimeEvent = (event, callback) => {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const { channel } = this.state;

      if (channel) {
        channel.bind(event, callback);
      }
    };

    /**
     * Unbinds event listeners to channels
     */
    stopListening = listener => {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const { channel } = this.state;

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      if (typeof listener === 'function' && Array.isArray(this.bindedListeners)) {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        this.bindedListeners = this.bindedListeners.filter(item => listener !== item);
      }

      if (channel) {
        channel.unbind();
        this.listen();
      }
    };

    /**
     * Store listener consumer listener for reinitialization upon reconnection
     */
    bindListener = listener => {
      if (typeof listener === 'function') {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        this.bindedListeners.push(listener);
        listener();
      }
    };

    render() {
      return (
        <Provider
          value={{
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            bindListener: this.bindListener,
            onRealtimeEvent: this.onRealtimeEvent,
            stopListening: this.stopListening,
          }}
        >
          <ChildComponent {...this.props} />
        </Provider>
      );
    }
  }

  return connect(
    state => ({
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      settings: state.settings,

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      account: state.profile,
    }),
    dispatch => ({
      updateNotificationCounts: counts => {
        dispatch({
          type: 'UPDATE_NOTIFICATION_COUNTS',
          payload: {
            data: {
              ...counts,
              has_fav_update: true,
            },
          },
        });
      },
      incrementUnreadOneTapOffersCountAction: () => {
        dispatch(incrementUnreadOneTapOffersCount());
        dispatch(resetNewOffers());
      },
      decrementUnreadOneTapOffersCountAction: () => {
        dispatch(decrementUnreadOneTapOffersCount());
        dispatch(resetNewOffers());
      },
      updateIdVerificationStatus: payload => {
        dispatch(updateIdVerificationStatus(payload));
      },
      updateUnreadMessagesCountProp: (payload: number) => {
        dispatch(updateUnreadMessagesCount(payload));
      },
    })
  )(PusherProvider);
};

export const withPusher = (ChildComponent: any): any => {
  return class extends PureComponent {
    render() {
      return <Consumer>{value => <ChildComponent {...value} {...this.props} />}</Consumer>;
    }
  };
};
